import { createSlice } from '@reduxjs/toolkit';

/**
 * Alert Instances slice (Alerts & Operations P3 — the Operations feed). Holds the org's SHARED
 * alert incidents for the active status filter + the triage lifecycle flags. Triage is saga-backed
 * (POST /alerts/instances/:id/{action}); the reducer adopts the server's returned incident and
 * drops it from the list when its new status leaves the current filter. Keyed by orgID.
 *
 * `summary` (AO2-2) is a SEPARATE always-active bucket (status='active', independent of whichever
 * status tab is currently selected) that the SummaryStrip counts against — so the strip reads
 * "what needs attention right now" regardless of which tab the admin is looking at.
 */
// AO2-1a status vocabulary (matches the App Server rename): assigned->investigating, snoozed->muted.
// Exported so Operations.js (the triage UI) shares this one definition of "still actionable"
// instead of re-deriving its own terminal-status set.
export const ACTIVE_STATUSES = ['open', 'investigating', 'escalated', 'muted'];

// AO2-2 SummaryStrip fetch cap (the API's own max — see listInstancesSchema). Shared by the saga
// (the actual fetch) and the selector (to detect the cap was hit and the counts are a floor).
export const SUMMARY_FETCH_LIMIT = 100;

// Keyset page size for the main status-tab list (B1/B2). The API default is 50 (max 100); we page
// through with `beforeID` + a "Load more" instead of fetching the whole bucket.
export const INSTANCES_PAGE_SIZE = 50;

const initialState = { byOrg: {}, error: null };

const ensure = (state, orgID) => {
	if (!state.byOrg[orgID])
		state.byOrg[orgID] = {
			items: [],
			status: 'active',
			loading: false,
			saving: false,
			loaded: false,
			// Keyset pagination for the current status tab: `nextCursor` is the last row's ID to pass as
			// the next `beforeID`; `exhausted` hides "Load more" once the end is reached.
			nextCursor: null,
			exhausted: false,
			loadingMore: false,
			summary: { items: [], loading: false, loaded: false, error: null },
			// A second always-on bucket for the terminal 'resolved' status — the active `summary` fetch
			// (status='active') excludes it, so the Resolved status-tab badge has no other count source.
			summaryResolved: { items: [], loading: false, loaded: false, error: null }
		};
	return state.byOrg[orgID];
};

// Does an incident still belong in the current filter after a status change?
const inFilter = (filter, status) => {
	if (filter === 'active') return ACTIVE_STATUSES.includes(status);
	if (!filter || filter === 'all') return true;
	return filter === status;
};

// Patch `list` (an Immer draft array) in place for a triaged incident: drop it if its new status
// no longer belongs under `filterStatus`, else update it. Shared by the tab-scoped list and the
// summary bucket so the same rule can't drift between the two.
const syncList = (list, filterStatus, inst) => {
	const i = list.findIndex((x) => x.alertInstanceID === inst.alertInstanceID);
	if (!inFilter(filterStatus, inst.status)) {
		if (i >= 0) list.splice(i, 1);
	} else if (i >= 0) {
		list[i] = inst;
	}
};

const alertInstancesSlice = createSlice({
	name: 'alertInstances',
	initialState,
	reducers: {
		loadInstances(state, action) {
			const s = ensure(state, action.payload.orgID);
			if (action.payload.status) s.status = action.payload.status;
			s.loading = true;
			state.error = null;
		},
		loadInstancesSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			s.items = action.payload.instances || [];
			s.nextCursor = action.payload.nextCursor ?? null;
			s.exhausted = action.payload.nextCursor == null;
			s.loading = false;
			s.loaded = true;
		},
		loadInstancesError(state, action) {
			ensure(state, action.payload.orgID).loading = false;
			state.error = action.payload.error;
		},

		// "Load more" — fetch the next keyset page (beforeID = current nextCursor) and APPEND it.
		loadMoreInstances(state, action) {
			ensure(state, action.payload.orgID).loadingMore = true;
		},
		loadMoreInstancesSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			s.loadingMore = false;
			// Drop the page if the user switched status tabs while this fetch was in flight — else its
			// rows (for the OLD tab) would be appended onto the new tab's list.
			if (action.payload.status && s.status !== action.payload.status) return;
			const more = action.payload.instances || [];
			// Dedupe by id — a concurrent insert can shift the keyset window and re-surface a row.
			const seen = new Set(s.items.map((x) => x.alertInstanceID));
			s.items.push(...more.filter((x) => !seen.has(x.alertInstanceID)));
			s.nextCursor = action.payload.nextCursor ?? null;
			// End reached when the server returns no cursor OR the page came back empty (the rare
			// full-final-page case where the previous cursor was non-null but there is nothing after).
			s.exhausted = action.payload.nextCursor == null || more.length === 0;
		},
		loadMoreInstancesError(state, action) {
			ensure(state, action.payload.orgID).loadingMore = false;
			state.error = action.payload.error;
		},

		loadSummary(state, action) {
			const sum = ensure(state, action.payload.orgID).summary;
			sum.loading = true;
			sum.error = null;
		},
		loadSummarySuccess(state, action) {
			const sum = ensure(state, action.payload.orgID).summary;
			sum.items = action.payload.instances || [];
			sum.loading = false;
			sum.loaded = true;
		},
		loadSummaryError(state, action) {
			const sum = ensure(state, action.payload.orgID).summary;
			sum.loading = false;
			sum.error = action.payload.error;
		},

		loadResolvedSummary(state, action) {
			const sum = ensure(state, action.payload.orgID).summaryResolved;
			sum.loading = true;
			sum.error = null;
		},
		loadResolvedSummarySuccess(state, action) {
			const sum = ensure(state, action.payload.orgID).summaryResolved;
			sum.items = action.payload.instances || [];
			sum.loading = false;
			sum.loaded = true;
		},
		loadResolvedSummaryError(state, action) {
			const sum = ensure(state, action.payload.orgID).summaryResolved;
			sum.loading = false;
			sum.error = action.payload.error;
		},

		triageInstance(state, action) {
			ensure(state, action.payload.orgID).saving = true;
		},
		triageInstanceSuccess(state, action) {
			const s = ensure(state, action.payload.orgID);
			s.saving = false;
			const inst = action.payload.instance;
			if (!inst) return;
			syncList(s.items, s.status, inst);
			syncList(s.summary.items, 'active', inst);
			// Keep the resolved-count bucket live too: resolving an incident adds it here, and
			// reopening one (should it ever move back out of 'resolved') drops it.
			syncList(s.summaryResolved.items, 'resolved', inst);
		},
		triageInstanceError(state, action) {
			ensure(state, action.payload.orgID).saving = false;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const alertInstancesActions = alertInstancesSlice.actions;
export default alertInstancesSlice.reducer;
